class ImagePreviewModule {
  constructor(options = {}) {
    this.options = {
      containerClass: 'cart-drawer-preview-btn',
      closeOnOutsideClick: true,
      closeOnEscape: true,
      showLoadingSpinner: true,
      maxWidth: '90vw',
      maxHeight: '90vh',
      animation: true,
      ...options
    };
    
    this.currentPopup = null;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    
    this.injectStyles();
    this.bindEvents();
    this.isInitialized = true;
  }

  injectStyles() {
    if (document.getElementById('image-preview-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'image-preview-styles';
    style.textContent = `
      .cart-drawer-preview-btn:hover {
        opacity: 0.7;
      }
      
      .preview-icon {
        width: 1.5rem;
        height: 1.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .preview-popup {
        position: fixed;
        z-index: 9999;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.8);
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .preview-popup.show {
        opacity: 1;
      }

      .preview-content {
        position: relative;
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        max-width: ${this.options.maxWidth};
        max-height: ${this.options.maxHeight};
        overflow: hidden;
        transform: scale(0.9);
        transition: transform 0.3s ease;
      }

      .preview-popup.show .preview-content {
        transform: scale(1);
      }

      .preview-header {
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: #f8f9fa;
      }

      .preview-title {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin: 0;
      }

      .preview-close {
        background: none;
        border: none;
        font-size: 24px;
        color: #666;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.2s ease;
      }

      .preview-close:hover {
        background: #e9ecef;
        color: #333;
      }

      .preview-image-container {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
      }

      .preview-image {
        max-width: 100%;
        max-height: calc(${this.options.maxHeight} - 80px);
        display: block;
        object-fit: contain;
      }

      .preview-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        color: #666;
      }

      .preview-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #007bff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      .preview-error {
        text-align: center;
        padding: 40px 20px;
        color: #dc3545;
      }

      .preview-error-icon {
        font-size: 48px;
        margin-bottom: 10px;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @media (max-width: 768px) {
        .preview-content {
          margin: 20px;
          max-width: calc(100% - 40px);
          max-height: calc(100% - 40px);
        }
        
        .preview-header {
          padding: 12px 15px;
        }
        
        .preview-title {
          font-size: 14px;
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(`.${this.options.containerClass}`);
      if (trigger) {
        event.preventDefault();
        this.showPreview(trigger);
      }
    });

    if (this.options.closeOnEscape) {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.currentPopup) {
          this.hidePreview();
        }
      });
    }
  }

  showPreview(trigger) {
    const src = trigger.getAttribute('data-preview-src');
    const alt = trigger.getAttribute('data-preview-alt') || 'Preview Image';
    
    if (!src) {
      console.warn('No preview source found');
      return;
    }

    this.hidePreview(); // Close any existing popup

    const popup = this.createPopup(src, alt);
    document.body.appendChild(popup);
    
    // Trigger animation
    requestAnimationFrame(() => {
      popup.classList.add('show');
    });

    this.currentPopup = popup;
  }

  createPopup(src, alt) {
    const popup = document.createElement('div');
    popup.className = 'preview-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-label', 'Image preview');

    popup.innerHTML = `
      <div class="preview-content">
        <div class="preview-header">
          <h3 class="preview-title">${this.escapeHtml(alt)}</h3>
          <button class="preview-close" aria-label="Close preview">&times;</button>
        </div>
        <div class="preview-image-container">
          ${this.options.showLoadingSpinner ? `
            <div class="preview-loading">
              <div class="preview-spinner"></div>
              <span>Loading...</span>
            </div>
          ` : ''}
        </div>
      </div>
    `;

    this.bindPopupEvents(popup, src, alt);
    return popup;
  }

  bindPopupEvents(popup, src, alt) {
    const closeBtn = popup.querySelector('.preview-close');
    const imageContainer = popup.querySelector('.preview-image-container');
    const loadingElement = popup.querySelector('.preview-loading');

    // Close button
    closeBtn.addEventListener('click', () => this.hidePreview());

    // Close on outside click
    if (this.options.closeOnOutsideClick) {
      popup.addEventListener('click', (event) => {
        if (event.target === popup) {
          this.hidePreview();
        }
      });
    }

    // Load image
    this.loadImage(src, alt, imageContainer, loadingElement);
  }

  loadImage(src, alt, container, loadingElement) {
    const img = new Image();
    
    img.onload = () => {
      if (loadingElement) {
        loadingElement.remove();
      }
      
      img.className = 'preview-image';
      img.alt = alt;
      container.appendChild(img);
    };

    img.onerror = () => {
      if (loadingElement) {
        loadingElement.remove();
      }
      
      container.innerHTML = `
        <div class="preview-error">
          <div class="preview-error-icon">⚠️</div>
          <p>Failed to load image</p>
          <small>${this.escapeHtml(src)}</small>
        </div>
      `;
    };

    img.src = src;
  }

  hidePreview() {
    if (!this.currentPopup) return;

    const popup = this.currentPopup;
    
    popup.classList.remove('show');
    
    setTimeout(() => {
      if (popup.parentNode) {
        popup.remove();
      }
    }, 300);

    this.currentPopup = null;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  destroy() {
    this.hidePreview();
    
    const styles = document.getElementById('image-preview-styles');
    if (styles) {
      styles.remove();
    }
    
    this.isInitialized = false;
  }
}

// Initialize immediately - event delegation works for dynamic content
function initializeModule() {
  const previewElements = document.querySelectorAll('.cart-drawer-preview-btn');
  console.log(`Initializing image preview module (found ${previewElements.length} preview elements)`);

  if (!window.imagePreview) {
    window.imagePreview = new ImagePreviewModule();
  }
}

// Check when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeModule);
} else {
  initializeModule();
}